import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { compatApi } from '../../utils/Intercepter';
import { format, differenceInCalendarDays } from 'date-fns';
import useTokenStore from '../../stores/tokens';
import { formatMonthDay } from '../../utils/dateUtils';
import { useFilteredPagination } from '../../utils/PageUtil';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import { useIsMobile } from '../../utils/responsive';
import OrgSectionTabs from '../../components/OrgSectionTabs';

// --- Sub-components from design ---

function MemberStatusBadge({ status }) {
    const map = {
        '승인 완료': { color: MINT, bg: hexToRgba(MINT, .14), dot: MINT },
        '승인완료': { color: MINT, bg: hexToRgba(MINT, .14), dot: MINT },
        '대기': { color: "#d97757", bg: "rgba(217,119,87,.14)", dot: "#d97757" },
        '반려': { color: "#c44569", bg: "rgba(196,69,105,.12)", dot: "#c44569" },
    };
    const s = map[status] || { color: "#5d5b6e", bg: "#f4f3f9", dot: "#aaa9b7" };
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 11.5, fontWeight: 600, color: s.color,
            background: s.bg, padding: "3px 9px", borderRadius: 999, lineHeight: 1.5,
            whiteSpace: "nowrap",
        }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot }} />
            {status}
        </span>
    );
}

function RoleBadge({ role }) {
    const isMaster = role === "MASTER";
    return (
        <span style={{
            display: "inline-flex", alignItems: "center",
            fontSize: 10.5, fontWeight: 700,
            color: isMaster ? PURPLE : "#5d5b6e",
            background: isMaster ? hexToRgba(PURPLE, .12) : "#f4f3f9",
            border: isMaster ? `1px solid ${hexToRgba(PURPLE, .20)}` : "1px solid #ececf2",
            padding: "3px 8px", borderRadius: 6, letterSpacing: ".06em",
            whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
        }}>{role}</span>
    );
}

function Avatar({ name, size = 28 }) {
    const initial = (name || "?").trim().slice(0, 1);
    let h = 0;
    for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
    const c1 = `hsl(${h}, 55%, 62%)`;
    const c2 = `hsl(${(h + 60) % 360}, 55%, 70%)`;
    return (
        <div style={{
            width: size, height: size, borderRadius: "50%",
            background: `linear-gradient(135deg, ${c1}, ${c2})`,
            color: "white", fontSize: size * 0.42, fontWeight: 700,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
        }}>{initial}</div>
    );
}

function StatTile({ label, value, suffix, accent, icon, highlight }) {
    return (
        <div style={{
            background: "white",
            borderRadius: 16,
            padding: "16px 18px",
            border: highlight ? `1.5px solid ${hexToRgba(accent, .35)}` : "1px solid #f0eff5",
            boxShadow: "0 1px 2px rgba(60,50,120,.04)",
            display: "flex", alignItems: "center", gap: 14,
            position: "relative", overflow: "hidden",
        }}>
            <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: hexToRgba(accent, .12), color: accent,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
            }}>{icon}</div>
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0 }}>
                <span style={{ fontSize: 11.5, color: "#8b8a99", fontWeight: 500, letterSpacing: ".03em" }}>{label}</span>
                <span style={{
                    fontSize: 22, fontWeight: 700, color: "#1f1d2e",
                    fontVariantNumeric: "tabular-nums", marginTop: 4,
                }}>
                    {value}
                    <span style={{ fontSize: 12, color: "#8b8a99", fontWeight: 500, marginLeft: 3 }}>{suffix}</span>
                </span>
            </div>
        </div>
    );
}

function PillGroup({ label, value, options, onChange }) {
    return (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: "#8b8a99", fontWeight: 600, letterSpacing: ".04em" }}>{label}</span>
            <div style={{ display: "inline-flex", gap: 4, background: "#f4f3f9", padding: 3, borderRadius: 10 }}>
                {options.map((opt) => {
                    const active = opt === value;
                    return (
                        <button key={opt} onClick={() => onChange(opt)} style={{
                            height: 28, padding: "0 12px", borderRadius: 7, border: "none",
                            background: active ? "white" : "transparent",
                            color: active ? "#1f1d2e" : "#8b8a99",
                            fontSize: 12, fontWeight: active ? 600 : 500,
                            cursor: active ? "default" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                            boxShadow: active ? "0 1px 3px rgba(60,50,120,.10)" : "none",
                            transition: "all 140ms",
                        }}>{opt}</button>
                    );
                })}
            </div>
        </div>
    );
}

const mtdStyle = (align = "left", muted = false) => ({
    padding: "14px 12px",
    textAlign: align,
    borderBottom: "1px solid #f4f3f9",
    fontSize: 12.5,
    color: muted ? "#5d5b6e" : "#3a3849",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
});

const thStyle = (align = "center", sortable = false) => ({
    padding: "12px 12px",
    fontSize: 12, fontWeight: 600, color: "#5d5b6e",
    textAlign: align,
    borderBottom: "1px solid #ececf2",
    background: "#f7f6fb",
    whiteSpace: "nowrap",
    letterSpacing: ".02em",
    cursor: sortable ? 'pointer' : 'default',
    userSelect: 'none',
});

const IconChevron = ({ width = 11, height = 11, style = {} }) => (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <path d="m9 18 6-6-6-6" />
    </svg>
);

export default function ReadEmployeeList() {
    const isMobile = useIsMobile(768);
    const { user } = useTokenStore();
    

    const [userList, setUserList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchWord, setSearchWord] = useState('');
    const [searchOption, setSearchOption] = useState('email');
    const [filterOpen, setFilterOpen] = useState(false);

    // Filter states for PillGroup
    const [roleFilter, setRoleFilter] = useState("전체");
    const [statusFilter, setStatusFilter] = useState("전체");
    const [positionRank, setPositionRank] = useState({}); // 직급명 → 서열(index)
    const [showDepartedModal, setShowDepartedModal] = useState(false);
    const [departedPage, setDepartedPage] = useState(1);
    const DEPARTED_PAGE_SIZE = 10;

    const pageLoadRef = useRef(false);
    // 마지막으로 실행한 검색 조건 — 승인/반려 등 처리 후 목록을 다시 불러올 때 검색 결과 화면이면 같은 조건으로 재조회한다
    const lastSearchRef = useRef(null);

    // resigned(퇴사 확정)만 퇴사자로 분류 — leaveDate만 있으면 "퇴사 예정"으로 재직 목록에 남는다
    const activeList = useMemo(() => userList.filter(u => !u.resigned), [userList]);
    const departedList = useMemo(() => userList.filter(u => !!u.resigned), [userList]);

    const filteredList = useMemo(() => {
        return activeList
            .filter(u => {
                if (roleFilter !== "전체" && u.userRole !== roleFilter) return false;
                if (statusFilter !== "전체") {
                    const status = u.approved ? "승인 완료" : (u.rejected ? "반려" : "대기");
                    if (status !== statusFilter) return false;
                } else if (u.rejected) {
                    // 가입 반려자는 조직 구성원이 아니므로 '전체'에서 빼고 '반려' 필터에서만 보여준다
                    return false;
                }
                return true;
            })
            // 직급 서열 정렬용 키(미등록/미설정 직급은 뒤로)
            .map(u => ({
                ...u,
                positionOrder: positionRank[u.position] ?? Number.MAX_SAFE_INTEGER,
                careerDays: u.totalCareerDays ?? 0, // 총 경력(이전 경력 + 현재 근속) 일수
                // 연봉협상일('MM-DD') 정렬용 키 — PageUtil 은 키 이름에 'Date'가 있으면 날짜로 변환해 정렬하는데
                // 연도 없는 'MM-DD'는 날짜 변환이 안 되므로 문자열 키로 따로 둔다('01-05' < '10-15' 로 월·일 순 정렬됨)
                negotiationMonthDay: u.negotiationDate || '',
            }))
            // 총 경력 내림차순으로 미리 정렬 → 이후 직급(positionOrder) 안정 정렬 시
            // 같은 직급 내에서는 경력 많은 순이 2차 기준으로 유지된다.
            .sort((a, b) => b.careerDays - a.careerDays);
    }, [activeList, roleFilter, statusFilter, positionRank]);

    const {
        currentItems,
        currentPage,
        totalPages,
        handlePageChange,
        handleNextGroup,
        handlePrevGroup,
        applySort,
        sortConfig,
        updateOriginalData,
    } = useFilteredPagination(filteredList, null, 10, 5, { key: 'positionOrder', direction: 'asc' });

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    const counts = useMemo(() => {
        const c = { total: 0, master: 0, staff: 0, pending: 0, departed: departedList.length };
        activeList.forEach((u) => {
            if (u.rejected) return; // 가입 반려자는 구성원 집계에서 제외
            c.total++;
            if (u.userRole === "MASTER") c.master++;
            if (u.userRole === "STAFF") c.staff++;
            if (!u.approved && !u.rejected) c.pending++;
        });
        return c;
    }, [activeList, departedList]);

    useEffect(() => {
        if (!user?.currentOrganizationCode) { setLoading(false); return; }
        const orgCode = user.currentOrganizationCode;
        if (pageLoadRef.current === orgCode) return;
        setLoading(true);
        lastSearchRef.current = null;
        fetchEmployeeList().finally(() => setLoading(false));
    }, [user?.currentOrganizationCode]); // eslint-disable-line react-hooks/exhaustive-deps

    // 직원 목록 조회 — 초기 로드와 승인/반려/삭제/퇴사 취소 후 갱신에서 공용으로 사용
    async function fetchEmployeeList() {
        try {
            const response = await compatApi.post('/user/readEmployeeList', user?.currentOrganizationCode);
            if (response.ok) {
                const data = response.data;
                setUserList(data);
                updateOriginalData(data);
            } else {
                alert('직원 목록을 가져오는 데 실패했습니다.');
            }
        } catch (error) {
            alert('오류가 발생했습니다.');
        }
    }

    // 처리 후 목록 갱신 — 페이지 전체 새로고침(window.location.reload) 대신 데이터만 다시 불러와
    // 상태/권한 필터·정렬·현재 페이지·검색 결과를 그대로 유지한다.
    const refreshList = async () => {
        if (lastSearchRef.current) {
            try {
                const response = await compatApi.post('/org/find/orgUser', lastSearchRef.current);
                if (response.ok) setUserList(response.data);
            } catch (error) {
                alert('목록을 다시 불러오는 중 오류가 발생했습니다.');
            }
        } else {
            await fetchEmployeeList();
        }
    };

    // 처리로 인원이 줄어 현재 페이지가 전체 페이지 수를 넘으면(예: 마지막 페이지의 마지막 1명 반려) 마지막 페이지로 맞춘다
    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) handlePageChange(totalPages);
    }, [totalPages, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

    // 조직 직급 서열 로드 → 직급 컬럼 정렬에 사용
    useEffect(() => {
        const orgCode = user?.currentOrganizationCode;
        if (!orgCode) return;
        (async () => {
            try {
                const res = await compatApi.get(`/org/positions/${orgCode}`);
                if (res.ok) {
                    const rank = {};
                    (res.data || []).forEach((p, i) => { rank[p.positionName] = i; });
                    setPositionRank(rank);
                }
            } catch { /* 실패 시 기존(문자열) 정렬로 폴백 */ }
        })();
    }, [user?.currentOrganizationCode]);

    const handleSearch = () => {
        const params = {
            organizationCode: user?.currentOrganizationCode || '',
            searchWord,
            searchOption,
            // 회원목록은 승인 대기자('대기')와 가입 반려자('반려' 필터)도 보여주므로 포함해서 조회
            includePending: true,
            includeRejected: true,
        };
        compatApi.post('/org/find/orgUser', params).then(response => {
            if (response.ok) {
                lastSearchRef.current = params;
                setUserList(response.data);
                handlePageChange(1);
            } else {
                alert('검색에 실패했습니다.');
            }
        }).catch(() => alert('오류가 발생했습니다.'));
    };

    // 서버가 내려준 안내 문구(409: 이미 처리된 요청 등)를 우선 노출
    const serverMessage = (error, fallback) => {
        const msg = error?.response?.data;
        return typeof msg === 'string' && msg ? msg : fallback;
    };

    const handleApprove = async (targetUser) => {
        if (!window.confirm(`${targetUser.userEmail} 회원의 조직 가입을 승인하시겠습니까?`)) return;
        try {
            const response = await compatApi.post('/org/approveUser', targetUser);
            if (response.ok) {
                const result = await response.text();
                alert(result);
                await refreshList();
            } else {
                alert(`회원가입 승인 중 오류가 발생했습니다. (${response.status})`);
            }
        } catch (error) {
            alert(serverMessage(error, `회원가입 승인 중 오류가 발생했습니다: ${error.message}`));
        }
    };

    const handleReject = async (targetUser) => {
        if (!window.confirm(`${targetUser.userEmail} 회원의 조직 가입을 반려하시겠습니까?\n\n반려된 회원은 [상태: 반려] 목록에서 삭제하기 전까지 다시 가입 신청할 수 없습니다.`)) return;
        try {
            const response = await compatApi.post('/org/rejectUser', targetUser);
            if (response.ok) {
                const result = await response.text();
                alert(result);
                await refreshList();
            } else {
                alert(`회원가입 반려 중 오류가 발생했습니다. (${response.status})`);
            }
        } catch (error) {
            alert(serverMessage(error, `회원가입 반려 중 오류가 발생했습니다: ${error.message}`));
        }
    };

    // 반려 회원 영구삭제 — 삭제 후 해당 회원은 이 조직에 다시 가입 신청할 수 있다.
    const handleDeleteRejected = async (targetUser) => {
        if (!window.confirm(`${targetUser.userEmail} 회원의 반려 기록을 삭제하시겠습니까?\n\n삭제하면 되돌릴 수 없으며, 해당 회원은 다시 가입 신청할 수 있습니다.`)) return;
        try {
            const response = await compatApi.post('/org/deleteRejectedUser', {
                userEmail: targetUser.userEmail,
                organizationCode: targetUser.organizationCode || user?.currentOrganizationCode,
            });
            if (response.ok) {
                alert(await response.text());
                await refreshList();
            } else {
                alert(`반려 회원 삭제 중 오류가 발생했습니다. (${response.status})`);
            }
        } catch (error) {
            alert(serverMessage(error, `반려 회원 삭제 중 오류가 발생했습니다: ${error.message}`));
        }
    };

    // 퇴사 취소 — 퇴사 예정(미래 leaveDate)·퇴사 확정 모두 leaveDate를 비워 복구한다.
    // 재가입 흐름과 달리 기존 직급·권한·승인 상태가 그대로 유지된다.
    const handleCancelLeave = async (targetUser) => {
        const label = targetUser.resigned ? '퇴사 처리' : '퇴사 예정';
        if (!window.confirm(`${targetUser.userEmail} 회원의 ${label}를 취소하시겠습니까?\n기존 직급·권한이 그대로 복구됩니다.`)) return;
        try {
            const response = await compatApi.post('/org/updateLeaveDate', {
                userEmail: targetUser.userEmail,
                organizationCode: targetUser.organizationCode || user?.currentOrganizationCode,
                leaveDate: null,
            });
            if (response.ok) {
                alert(`${label}가 취소되었습니다.`);
                await refreshList();
            } else {
                alert(`${label} 취소 중 오류가 발생했습니다. (${response.status})`);
            }
        } catch (error) {
            alert(`네트워크 오류가 발생했습니다: ${error.message}`);
        }
    };

    const isMaster = user?.currentRole === 'MASTER';

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 61px)', background: '#f5f4fa' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${hexToRgba(PURPLE, 0.2)}`, borderTopColor: PURPLE, animation: 'spin 0.7s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    const searchOptionsMap = {
        'email': '이메일',
        'name': '성명',
        'role': '권한'
    };

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

                {/* 조직 섹션 탭 (조직정보·회원목록·직급관리 / 생성·가입) */}
                <OrgSectionTabs active="org-members" />

                {/* Stat tiles */}
                <div style={{
                    display: "grid", gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 16,
                }}>
                    <StatTile label="재직 회원" value={counts.total} suffix="명" accent={PURPLE}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.5" /><path d="M2 21c0-3.5 3-6 7-6s7 2.5 7 6" /><circle cx="17" cy="7" r="2.5" /><path d="M22 18c0-2.5-2-4-4.5-4" /></svg>}
                    />
                    <StatTile label="MASTER" value={counts.master} suffix="명" accent={PURPLE}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.4 5 5.6.8-4 3.9 1 5.5L12 14.8 7 17.2l1-5.5L4 7.8 9.6 7z" /></svg>}
                    />
                    <StatTile label="STAFF" value={counts.staff} suffix="명" accent={MINT}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="9" r="4" /><path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" /></svg>}
                    />
                    <StatTile label="승인 대기" value={counts.pending} suffix="건" accent="#d97757"
                        highlight={counts.pending > 0}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>}
                    />
                </div>

                {/* Search & filter row */}
                <div style={{
                    background: "white", borderRadius: 16, padding: "16px 20px",
                    border: "1px solid #f0eff5", boxShadow: "0 1px 2px rgba(60,50,120,.04)",
                    marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 16, flexWrap: "wrap",
                }}>
                    {/* Left: pill filters */}
                    <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
                        <PillGroup
                            label="권한"
                            value={roleFilter}
                            options={["전체", "MASTER", "STAFF"]}
                            onChange={setRoleFilter}
                        />
                        <span style={{ width: 1, height: 22, background: "#ececf2" }} />
                        <PillGroup
                            label="상태"
                            value={statusFilter}
                            options={["전체", "승인 완료", "대기", "반려"]}
                            onChange={setStatusFilter}
                        />
                    </div>

                    {/* Right: search */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
                        <div style={{ position: "relative" }}>
                            <button
                                onClick={() => setFilterOpen(!filterOpen)}
                                style={{
                                    height: 38, padding: "0 14px", paddingRight: 30,
                                    borderRadius: 10, background: "#fafaff",
                                    border: "1.5px solid #ececf2",
                                    fontSize: 13, color: "#3a3849", fontWeight: 500,
                                    cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                    position: "relative",
                                }}>
                                {searchOptionsMap[searchOption]}
                                <IconChevron style={{
                                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%) rotate(90deg)", color: "#8b8a99",
                                }} />
                            </button>
                            {filterOpen && (
                                <>
                                    <div onClick={() => setFilterOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 5 }} />
                                    <div style={{
                                        position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 6,
                                        background: "white", borderRadius: 10,
                                        boxShadow: "0 8px 24px -8px rgba(60,50,120,.20)",
                                        border: "1px solid #ececf2", minWidth: 100,
                                        overflow: "hidden",
                                    }}>
                                        {Object.entries(searchOptionsMap).map(([val, label]) => (
                                            <button key={val} onClick={() => { setSearchOption(val); setFilterOpen(false); }} style={{
                                                display: "block", width: "100%", textAlign: "left",
                                                padding: "9px 14px", background: searchOption === val ? hexToRgba(PURPLE, .08) : "white",
                                                border: "none", color: searchOption === val ? PURPLE : "#3a3849",
                                                fontSize: 13, fontWeight: searchOption === val ? 600 : 500, cursor: "pointer",
                                                fontFamily: "inherit",
                                            }}>{label}</button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <input
                            type="text"
                            value={searchWord}
                            onChange={(e) => setSearchWord(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                            placeholder="검색어를 입력 하세요"
                            style={{
                                height: 38, width: 280, borderRadius: 10, padding: "0 14px",
                                border: "1.5px solid #ececf2", background: "white",
                                fontSize: 13, color: "#1f1d2e", outline: "none", fontFamily: "inherit",
                                transition: "border-color 140ms, box-shadow 140ms",
                            }}
                            onFocus={(e) => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, .10)}`; }}
                            onBlur={(e) => { e.target.style.borderColor = "#ececf2"; e.target.style.boxShadow = "none"; }}
                        />

                        <button
                            onClick={handleSearch}
                            style={{
                                height: 38, padding: "0 22px", borderRadius: 10, border: "none",
                                background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                color: "white", fontSize: 13, fontWeight: 600,
                                cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                                boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`,
                                display: "inline-flex", alignItems: "center", gap: 6,
                            }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                            </svg>
                            검색
                        </button>
                    </div>
                </div>

                {/* Main table card */}
                <div style={{
                    background: "white", borderRadius: 16,
                    border: "1px solid #f0eff5",
                    boxShadow: "0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)",
                    overflow: "hidden",
                }}>
                    {/* Card header */}
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "16px 20px",
                        background: "linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)",
                        borderBottom: "1px solid #ececf2",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "#1f1d2e" }}>회원 목록</h2>
                            <span style={{
                                fontSize: 11, fontWeight: 600,
                                background: "white", color: "#5d5b6e",
                                padding: "2px 8px", borderRadius: 999, lineHeight: 1.45,
                                border: "1px solid #ececf2",
                            }}>총 {filteredList.length}건</span>
                        </div>
                        <button onClick={() => { setDepartedPage(1); setShowDepartedModal(true); }} style={{
                            height: 32, padding: "0 14px", borderRadius: 8,
                            border: `1.5px solid ${hexToRgba("#c44569", .25)}`,
                            background: hexToRgba("#c44569", .06),
                            color: "#c44569", fontSize: 12.5, fontWeight: 600,
                            cursor: "pointer", fontFamily: "inherit",
                            display: "inline-flex", alignItems: "center", gap: 6,
                            transition: "all 140ms",
                        }}
                            onMouseEnter={e => { e.currentTarget.style.background = hexToRgba("#c44569", .12); }}
                            onMouseLeave={e => { e.currentTarget.style.background = hexToRgba("#c44569", .06); }}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                            </svg>
                            퇴사자 관리
                            {counts.departed > 0 && (
                                <span style={{
                                    fontSize: 10, fontWeight: 700, lineHeight: 1,
                                    background: "#c44569", color: "white",
                                    padding: "2px 6px", borderRadius: 999,
                                }}>{counts.departed}</span>
                            )}
                        </button>
                    </div>

                    {/* Table */}
                    <div style={{ overflowX: "auto" }}>
                        <table style={{
                            width: "100%", borderCollapse: "separate", borderSpacing: 0,
                            fontSize: 13, color: "#3a3849", minWidth: 1400,
                        }}>
                            <thead>
                                <tr>
                                    <th style={thStyle("center")}>번호</th>
                                    <th style={thStyle("left", true)} onClick={() => applySort('userEmail')}>이메일{getSortIcon('userEmail')}</th>
                                    <th style={thStyle("left", true)} onClick={() => applySort('userName')}>성명{getSortIcon('userName')}</th>
                                    <th style={thStyle("center", true)} onClick={() => applySort('positionOrder')}>직급{getSortIcon('positionOrder')}</th>
                                    <th style={thStyle("center", true)} onClick={() => applySort('userRole')}>권한{getSortIcon('userRole')}</th>
                                    <th style={thStyle("center", true)} onClick={() => applySort('joinDate')}>입사일{getSortIcon('joinDate')}</th>
                                    <th style={thStyle("center", true)} onClick={() => applySort('negotiationMonthDay')}>연봉협상일{getSortIcon('negotiationMonthDay')}</th>
                                    <th style={thStyle("center")}>자격증</th>
                                    <th style={thStyle("center", true)} onClick={() => applySort('lastLogin')}>마지막 로그인{getSortIcon('lastLogin')}</th>
                                    <th style={thStyle("center", true)} onClick={() => applySort('leaveDate')}>퇴사일{getSortIcon('leaveDate')}</th>
                                    <th style={thStyle("center")}>기능</th>
                                    <th style={thStyle("center")}>승인 / 반려</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((u, idx) => {
                                    const isMe = user?.userEmail === u.userEmail;
                                    const rowBg = isMe ? "#eef4fb" : "white";
                                    const rowHover = isMe ? "#e5eef9" : "#fafaff";

                                    const statusLabel = u.approved ? "승인 완료" : (u.rejected ? "반려" : "대기");

                                    let sortedCerts = u.certificates || "";
                                    if (typeof sortedCerts === 'string' && sortedCerts.includes('정보처리기사')) {
                                        const arr = sortedCerts.split(',').map(s => s.trim());
                                        const master = arr.filter(c => c.includes('정보처리기사'));
                                        const other = arr.filter(c => !c.includes('정보처리기사'));
                                        sortedCerts = [...master, ...other].join(', ');
                                    }

                                    return (
                                        <tr key={u.userEmail}
                                            style={{ background: rowBg, transition: "background 100ms" }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = rowHover}
                                            onMouseLeave={(e) => e.currentTarget.style.background = rowBg}
                                        >
                                            <td style={mtdStyle("center")}>
                                                <span style={{ color: "#8b8a99" }}>{(currentPage - 1) * 10 + idx + 1}</span>
                                            </td>
                                            <td style={mtdStyle("left")}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <Avatar name={u.userName} />
                                                    <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
                                                        <span style={{
                                                            color: "#1f1d2e", fontWeight: 500,
                                                            fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                        }}>{u.userEmail}</span>
                                                        {isMe && (
                                                            <span style={{
                                                                fontSize: 9.5, fontWeight: 700, color: "#2c7dd0",
                                                                background: "#d8e6f6", padding: "1px 6px", borderRadius: 4,
                                                                letterSpacing: ".04em", alignSelf: "flex-start", marginTop: 2,
                                                            }}>ME</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={mtdStyle("left")}>
                                                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                                    <span style={{ color: "#1f1d2e", fontWeight: 500 }}>{u.userName}</span>
                                                    {u.userNameEn && (
                                                        <span style={{ fontSize: 11, color: "#8b8a99", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.userNameEn}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={mtdStyle("center")}>
                                                <span style={{ color: !u.position ? "#c7c5d2" : "#3a3849" }}>{u.position || "—"}</span>
                                            </td>
                                            <td style={mtdStyle("center")}>
                                                <RoleBadge role={u.userRole} />
                                            </td>
                                            <td style={mtdStyle("center", true)}>
                                                <span style={{ color: !u.joinDate ? "#c7c5d2" : "#5d5b6e" }}>
                                                    {u.joinDate ? format(new Date(u.joinDate), 'yyyy-MM-dd') : "—"}
                                                </span>
                                            </td>
                                            <td style={mtdStyle("center", true)}>
                                                <span style={{ color: !u.negotiationDate ? "#c7c5d2" : "#5d5b6e" }}>
                                                    {u.negotiationDate ? formatMonthDay(u.negotiationDate) : "—"}
                                                </span>
                                            </td>
                                            <td style={mtdStyle("center")}>
                                                {!sortedCerts ? (
                                                    <span style={{ color: "#c7c5d2" }}>—</span>
                                                ) : (
                                                    <span style={{
                                                        fontSize: 11.5, color: "#5d5b6e", fontWeight: 500,
                                                        background: "#f4f3f9", padding: "2px 8px", borderRadius: 6,
                                                    }}>{sortedCerts}</span>
                                                )}
                                            </td>
                                            <td style={mtdStyle("center", true)}>
                                                <span style={{ color: !u.lastLogin ? "#c7c5d2" : "#5d5b6e", fontSize: 12 }}>
                                                    {u.lastLogin ? format(new Date(u.lastLogin), 'yyyy-MM-dd HH:mm:ss') : "—"}
                                                </span>
                                            </td>
                                            <td style={mtdStyle("center", true)}>
                                                {u.leaveDate ? (
                                                    // 재직 목록에 leaveDate가 보이면 아직 확정 전인 "퇴사 예정자"
                                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                                        <span style={{ color: "#d97757", fontWeight: 600 }}>
                                                            {format(new Date(u.leaveDate), 'yyyy-MM-dd')}
                                                        </span>
                                                        <span style={{
                                                            fontSize: 10, fontWeight: 700, color: "#d97757",
                                                            background: "rgba(217,119,87,.14)", padding: "2px 6px",
                                                            borderRadius: 999, whiteSpace: "nowrap",
                                                        }}>
                                                            예정{(() => {
                                                                const d = differenceInCalendarDays(new Date(u.leaveDate), new Date());
                                                                return d > 0 ? ` D-${d}` : '';
                                                            })()}
                                                        </span>
                                                    </span>
                                                ) : (
                                                    <span style={{ color: "#c7c5d2" }}>—</span>
                                                )}
                                            </td>
                                            <td style={mtdStyle("center")}>
                                                <div style={{ display: "inline-flex", gap: 6 }}>
                                                    {isMaster && (
                                                        <Link to="/viewResumePage" state={{ ...u }} style={{
                                                            height: 28, padding: "0 10px", borderRadius: 7,
                                                            background: "white", border: "1px solid #ececf2",
                                                            color: "#5d5b6e", fontSize: 11.5, fontWeight: 600,
                                                            textDecoration: "none", display: "inline-flex", alignItems: "center",
                                                            transition: "all 120ms",
                                                        }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.background = "#fafaff"; }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.background = "white"; }}
                                                        >상세 정보</Link>
                                                    )}
                                                    {isMaster && u.leaveDate && (
                                                        <button onClick={() => handleCancelLeave(u)} style={{
                                                            height: 28, padding: "0 10px", borderRadius: 7,
                                                            background: "white", border: "1px solid rgba(217,119,87,.35)",
                                                            color: "#d97757", fontSize: 11.5, fontWeight: 600,
                                                            cursor: "pointer", fontFamily: "inherit",
                                                            display: "inline-flex", alignItems: "center",
                                                        }}>예정 취소</button>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={mtdStyle("center")}>
                                                {isMaster && !u.approved && !u.rejected ? (
                                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                        <button onClick={() => handleApprove(u)} style={{
                                                            padding: '4px 10px', borderRadius: 6, border: 'none',
                                                            background: '#dcfce7', color: '#15803d',
                                                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                                        }}>승인</button>
                                                        <button onClick={() => handleReject(u)} style={{
                                                            padding: '4px 10px', borderRadius: 6, border: 'none',
                                                            background: '#fee2e2', color: '#dc2626',
                                                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                                        }}>반려</button>
                                                    </div>
                                                ) : isMaster && u.rejected ? (
                                                    <div
                                                        title={`반려일시: ${u.rejectedAt ? format(new Date(u.rejectedAt), 'yyyy-MM-dd HH:mm') : '-'}${u.rejectedBy ? `\n반려자: ${u.rejectedBy}` : ''}`}
                                                        style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}
                                                    >
                                                        <MemberStatusBadge status={statusLabel} />
                                                        {/* 근무 이력(입사일)이 있는 퇴사자 재가입 반려 건은 인사 기록 보호를 위해 서버가 삭제를 거부한다 */}
                                                        {!u.joinDate && (
                                                            <button onClick={() => handleDeleteRejected(u)} style={{
                                                                padding: '4px 10px', borderRadius: 6, border: '1px solid #fecaca',
                                                                background: 'white', color: '#dc2626',
                                                                fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                                            }}>삭제</button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <MemberStatusBadge status={statusLabel} />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {currentItems.length === 0 && (
                                    <tr>
                                        <td colSpan={12} style={{ padding: "48px 20px", textAlign: "center", color: "#aaa9b7", fontSize: 13 }}>
                                            조건에 맞는 회원이 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "14px 20px", borderTop: "1px solid #ececf2",
                        background: "#fafaff",
                    }}>
                        <span style={{ fontSize: 12, color: "#8b8a99" }}>
                            전체 {filteredList.length}건
                        </span>
                        {totalPages > 1 && (
                            <div style={{ display: "flex", gap: 4 }}>
                                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} style={pageBtn(currentPage === 1)}>«</button>
                                <button onClick={handlePrevGroup} disabled={currentPage === 1} style={pageBtn(currentPage === 1)}>‹</button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(n => n >= Math.max(1, currentPage - 2) && n <= Math.min(totalPages, currentPage + 2))
                                    .map(n => (
                                        <button key={n} onClick={() => handlePageChange(n)} style={pageBtn(false, n === currentPage)}>
                                            {n}
                                        </button>
                                    ))}
                                <button onClick={handleNextGroup} disabled={currentPage === totalPages} style={pageBtn(currentPage === totalPages)}>›</button>
                                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} style={pageBtn(currentPage === totalPages)}>»</button>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* 퇴사자 모달 */}
            {showDepartedModal && (
                <div onClick={() => setShowDepartedModal(false)} style={{
                    position: "fixed", inset: 0, zIndex: 100,
                    background: "rgba(30,25,60,.45)", backdropFilter: "blur(2px)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "24px 16px",
                }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        background: "white", borderRadius: 20, width: "100%", maxWidth: 1200,
                        maxHeight: "calc(100vh - 80px)", display: "flex", flexDirection: "column",
                        boxShadow: "0 24px 64px -16px rgba(30,25,60,.35)",
                        overflow: "hidden",
                    }}>
                        {/* 모달 헤더 */}
                        <div style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "18px 24px", borderBottom: "1px solid #ececf2",
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1f1d2e" }}>퇴사자 목록</h2>
                                <span style={{
                                    fontSize: 11, fontWeight: 600, background: hexToRgba("#c44569", .1),
                                    color: "#c44569", padding: "2px 8px", borderRadius: 999, lineHeight: 1.45,
                                }}>{departedList.length}명</span>
                            </div>
                            <button onClick={() => setShowDepartedModal(false)} style={{
                                width: 30, height: 30, borderRadius: 8, border: "1px solid #ececf2",
                                background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#5d5b6e",
                            }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {/* 모달 테이블 */}
                        <div style={{ overflowY: "auto", flex: 1 }}>
                            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 13, color: "#3a3849", minWidth: 1000 }}>
                                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                    <tr>
                                        <th style={thStyle("center")}>번호</th>
                                        <th style={thStyle("left")}>이메일</th>
                                        <th style={thStyle("left")}>성명</th>
                                        <th style={thStyle("center")}>직급</th>
                                        <th style={thStyle("center")}>권한</th>
                                        <th style={thStyle("center")}>입사일</th>
                                        <th style={thStyle("center")}>퇴사일</th>
                                        <th style={thStyle("center")}>유형</th>
                                        <th style={thStyle("left")}>사유</th>
                                        <th style={thStyle("center")}>처리 정보</th>
                                        {isMaster && <th style={thStyle("center")}>기능</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {departedList.slice((departedPage - 1) * DEPARTED_PAGE_SIZE, departedPage * DEPARTED_PAGE_SIZE).map((u, idx) => (
                                        <tr key={u.userEmail} style={{ background: "white" }}
                                            onMouseEnter={e => e.currentTarget.style.background = "#fafaff"}
                                            onMouseLeave={e => e.currentTarget.style.background = "white"}
                                        >
                                            <td style={mtdStyle("center")}><span style={{ color: "#8b8a99" }}>{(departedPage - 1) * DEPARTED_PAGE_SIZE + idx + 1}</span></td>
                                            <td style={mtdStyle("left")}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <Avatar name={u.userName} size={24} />
                                                    <span style={{ color: "#5d5b6e" }}>{u.userEmail}</span>
                                                </div>
                                            </td>
                                            <td style={mtdStyle("left")}>
                                                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                                    <span style={{ color: "#1f1d2e", fontWeight: 500 }}>{u.userName}</span>
                                                    {u.userNameEn && (
                                                        <span style={{ fontSize: 11, color: "#8b8a99", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.userNameEn}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={mtdStyle("center")}><span style={{ color: !u.position ? "#c7c5d2" : "#3a3849" }}>{u.position || "—"}</span></td>
                                            <td style={mtdStyle("center")}><RoleBadge role={u.userRole} /></td>
                                            <td style={mtdStyle("center", true)}>
                                                <span style={{ color: !u.joinDate ? "#c7c5d2" : "#5d5b6e" }}>
                                                    {u.joinDate ? format(new Date(u.joinDate), 'yyyy-MM-dd') : "—"}
                                                </span>
                                            </td>
                                            <td style={mtdStyle("center", true)}>
                                                <span style={{ color: "#c44569", fontWeight: 600 }}>
                                                    {format(new Date(u.leaveDate), 'yyyy-MM-dd')}
                                                </span>
                                            </td>
                                            <td style={mtdStyle("center")}>
                                                {u.leaveType ? (
                                                    <span style={{
                                                        fontSize: 11, fontWeight: 600, color: "#c44569",
                                                        background: hexToRgba("#c44569", .08),
                                                        padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap",
                                                    }}>{u.leaveType}</span>
                                                ) : (
                                                    <span style={{ color: "#c7c5d2" }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ ...mtdStyle("left", true), whiteSpace: "normal", maxWidth: 220 }}>
                                                {u.leaveReason ? (
                                                    <span title={u.leaveReason} style={{
                                                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                                        overflow: "hidden", fontSize: 12, color: "#5d5b6e",
                                                    }}>{u.leaveReason}</span>
                                                ) : (
                                                    <span style={{ color: "#c7c5d2" }}>—</span>
                                                )}
                                            </td>
                                            <td style={mtdStyle("center", true)}>
                                                {u.leaveProcessedBy ? (
                                                    <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.4 }}>
                                                        <span style={{ fontSize: 11.5, color: "#5d5b6e" }}>{u.leaveProcessedBy}</span>
                                                        {u.leaveProcessedAt && (
                                                            <span style={{ fontSize: 10.5, color: "#aaa9b7" }}>
                                                                {format(new Date(u.leaveProcessedAt), 'yyyy-MM-dd HH:mm')}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span style={{ color: "#c7c5d2" }}>—</span>
                                                )}
                                            </td>
                                            {isMaster && (
                                                <td style={mtdStyle("center")}>
                                                    <div style={{ display: "inline-flex", gap: 6 }}>
                                                        <Link to="/viewResumePage" state={{ ...u }} style={{
                                                            height: 28, padding: "0 10px", borderRadius: 7,
                                                            background: "white", border: "1px solid #ececf2",
                                                            color: "#5d5b6e", fontSize: 11.5, fontWeight: 600,
                                                            textDecoration: "none", display: "inline-flex", alignItems: "center",
                                                        }}>상세 정보</Link>
                                                        <button onClick={() => handleCancelLeave(u)} style={{
                                                            height: 28, padding: "0 10px", borderRadius: 7,
                                                            background: "white", border: `1px solid ${hexToRgba("#c44569", .35)}`,
                                                            color: "#c44569", fontSize: 11.5, fontWeight: 600,
                                                            cursor: "pointer", fontFamily: "inherit",
                                                            display: "inline-flex", alignItems: "center",
                                                        }}>퇴사 취소</button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                    {departedList.length === 0 && (
                                        <tr><td colSpan={11} style={{ padding: "48px", textAlign: "center", color: "#aaa9b7", fontSize: 13 }}>퇴사자가 없습니다.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* 모달 footer */}
                        <div style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "14px 20px", borderTop: "1px solid #ececf2", background: "#fafaff",
                        }}>
                            <span style={{ fontSize: 12, color: "#8b8a99" }}>전체 {departedList.length}명</span>
                            {Math.ceil(departedList.length / DEPARTED_PAGE_SIZE) > 1 && (
                                <div style={{ display: "flex", gap: 4 }}>
                                    <button onClick={() => setDepartedPage(1)} disabled={departedPage === 1} style={pageBtn(departedPage === 1)}>«</button>
                                    <button onClick={() => setDepartedPage(p => Math.max(1, p - 1))} disabled={departedPage === 1} style={pageBtn(departedPage === 1)}>‹</button>
                                    {Array.from({ length: Math.ceil(departedList.length / DEPARTED_PAGE_SIZE) }, (_, i) => i + 1)
                                        .filter(n => n >= Math.max(1, departedPage - 2) && n <= Math.min(Math.ceil(departedList.length / DEPARTED_PAGE_SIZE), departedPage + 2))
                                        .map(n => (
                                            <button key={n} onClick={() => setDepartedPage(n)} style={pageBtn(false, n === departedPage)}>{n}</button>
                                        ))}
                                    <button onClick={() => setDepartedPage(p => Math.min(Math.ceil(departedList.length / DEPARTED_PAGE_SIZE), p + 1))} disabled={departedPage === Math.ceil(departedList.length / DEPARTED_PAGE_SIZE)} style={pageBtn(departedPage === Math.ceil(departedList.length / DEPARTED_PAGE_SIZE))}>›</button>
                                    <button onClick={() => setDepartedPage(Math.ceil(departedList.length / DEPARTED_PAGE_SIZE))} disabled={departedPage === Math.ceil(departedList.length / DEPARTED_PAGE_SIZE)} style={pageBtn(departedPage === Math.ceil(departedList.length / DEPARTED_PAGE_SIZE))}>»</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function pageBtn(disabled, active = false) {
    return {
        minWidth: 32, height: 32, borderRadius: 8,
        border: active ? 'none' : '1.5px solid #ececf2',
        background: active ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : disabled ? '#fafaff' : 'white',
        color: active ? 'white' : disabled ? '#c7c5d2' : '#3a3849',
        fontSize: 13, fontWeight: active ? 600 : 500, cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit',
    };
}
